import { useCallback, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'

import type {
    PlaybackSegment,
    VehicleGpsDetail,
} from '@/lib/api/vehicle-gps-api'
import type { MapMode } from '@/components/vehicles/vehicle-gps-map'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { VehicleForm } from '@/components/forms/vehicle-form'
import { VehicleCurrentTripCard } from '@/components/vehicles/vehicle-current-trip-card'
import { VehicleDetailHeader } from '@/components/vehicles/vehicle-detail-header'
import { VehicleGpsCard } from '@/components/vehicles/vehicle-gps-card'
import { VehicleInfoCard } from '@/components/vehicles/vehicle-info-card'
import { VehiclePlaybackPanel } from '@/components/vehicles/vehicle-playback-panel'
import { VehicleCameraPanel } from '@/components/vehicles/vehicle-camera-panel'
import { useActiveTripByVehicle } from '@/hooks/use-delivery-trips-query'
import { useAuth } from '@/hooks/use-auth'
import {
    useResolveVehicleGps,
    useVehicleGps,
} from '@/hooks/use-vehicle-gps-query'
import { useVehicle } from '@/hooks/use-vehicles-query'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/vehicles/$vehicleId',
)({
    component: VehicleDetailPage,
})

function gpsErrorText(detail: VehicleGpsDetail | null | undefined) {
    switch (detail?.gps_error) {
        case 'no_gps':
            return m.vehicle_gps_state_no_gps()
        case 'disabled':
            return m.tracking_disabled_banner()
        case 'not_found':
            return m.vehicle_gps_state_not_found()
        case 'upstream':
            return m.vehicle_gps_state_upstream()
        default:
            return null
    }
}

function VehicleDetailPage() {
    const { vehicleId } = Route.useParams()
    const navigate = useNavigate()
    const { hasPermission } = useAuth()
    const canUpdate = hasPermission('vehicles.update')
    const [editOpen, setEditOpen] = useState(false)
    const [vehicleInfoOpen, setVehicleInfoOpen] = useState(false)
    const [playbackSegments, setPlaybackSegments] = useState<PlaybackSegment[]>(
        [],
    )
    const [selectedPlaybackIndex, setSelectedPlaybackIndex] = useState<
        number | null
    >(null)

    const { data: vehicle, isLoading: vehicleLoading } = useVehicle(vehicleId)
    const gpsQuery = useVehicleGps(vehicleId, Boolean(vehicle))
    const resolveGps = useResolveVehicleGps(vehicleId)
    // Chỉ fetch active trip khi vehicle status = in_use — tiết kiệm 1 query
    // rác cho các vehicle available/maintenance/retired (chắc chắn không có
    // active trip do UNIQUE partial index enforce). Khi vehicle nhảy sang
    // in_use, staleTime = 30s + refetchInterval = 60s tự cập nhật.
    const activeTripQuery = useActiveTripByVehicle(
        vehicleId,
        vehicle?.status === 'in_use',
    )

    const handleSegmentsLoaded = useCallback((segments: PlaybackSegment[]) => {
        setPlaybackSegments(segments)
    }, [])

    const handleSegmentSelected = useCallback((index: number | null) => {
        setSelectedPlaybackIndex(index)
    }, [])

    const handleBackToLive = useCallback(() => {
        setSelectedPlaybackIndex(null)
        setPlaybackSegments([])
    }, [])

    // Derive map mode from state:
    // - no loaded segments → live
    // - segments loaded, nothing selected → all
    // - segment selected → segment
    const mapMode: MapMode =
        playbackSegments.length === 0
            ? 'live'
            : selectedPlaybackIndex === null
              ? 'all'
              : 'segment'

    if (vehicleLoading) {
        return (
            <div className="py-8 text-center text-sm text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }

    if (!vehicle) {
        return (
            <div className="py-8 text-center text-sm text-destructive">
                {m.vehicle_not_found()}
            </div>
        )
    }

    const gpsDetail = gpsQuery.data
    const hasGpsConfigured = Boolean(vehicle.gps_provider && vehicle.gps_imei)
    const shouldShowResolve =
        canUpdate &&
        hasGpsConfigured &&
        (!vehicle.gps_device_id || gpsDetail?.gps_error === 'not_found')

    // Combined GPS + playback card content, reused in both layout branches.
    const gpsAndPlaybackContent = (
        <>
            <VehicleGpsCard
                gpsDetail={gpsDetail}
                gpsError={gpsErrorText(gpsDetail)}
                isLoading={gpsQuery.isLoading}
                mode={mapMode}
                segments={playbackSegments}
                selectedSegmentIndex={selectedPlaybackIndex}
                onBackToLive={handleBackToLive}
            />

            <Card>
                <CardHeader>
                    <CardTitle>{m.tracking_playback_title()}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <VehiclePlaybackPanel
                        vehicleId={vehicleId}
                        onSegmentsLoaded={handleSegmentsLoaded}
                        selectedSegmentIndex={selectedPlaybackIndex}
                        onSegmentSelected={handleSegmentSelected}
                        onSegmentsReemit={handleSegmentsLoaded}
                    />
                </CardContent>
            </Card>
        </>
    )

    return (
        <div className="space-y-4">
            <VehicleDetailHeader
                vehicle={vehicle}
                gpsQuery={gpsQuery}
                resolveGpsPending={resolveGps.isPending}
                canUpdate={canUpdate}
                vehicleInfoOpen={vehicleInfoOpen}
                shouldShowResolve={shouldShowResolve}
                // Back goes to the vehicle list, which moved out of the
                // tracking screen and back to its own /vehicles page.
                onBack={() => navigate({ to: '/vehicles' })}
                onToggleInfo={() => setVehicleInfoOpen((o) => !o)}
                onRefreshGps={() => void gpsQuery.refetch()}
                onResolveGps={() => resolveGps.mutate()}
                onEdit={() => setEditOpen(true)}
            />

            {activeTripQuery.data && (
                <VehicleCurrentTripCard
                    trip={activeTripQuery.data}
                    vehicleId={vehicleId}
                />
            )}

            {vehicleInfoOpen ? (
                /* Two-column layout when info card is open:
                   left = 320px info card, right = GPS + playback.
                   Camera always full-width below. */
                <>
                    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
                        <VehicleInfoCard vehicle={vehicle} />
                        <div className="space-y-4">{gpsAndPlaybackContent}</div>
                    </div>
                    <Card>
                        <CardHeader>
                            <CardTitle>{m.vehicle_camera_title()}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <VehicleCameraPanel
                                vehicleId={vehicleId}
                                enabled={hasGpsConfigured}
                            />
                        </CardContent>
                    </Card>
                </>
            ) : (
                /* Single-column: GPS + playback, then camera at bottom. */
                <div className="space-y-4">
                    {gpsAndPlaybackContent}
                    <Card>
                        <CardHeader>
                            <CardTitle>{m.vehicle_camera_title()}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <VehicleCameraPanel
                                vehicleId={vehicleId}
                                enabled={hasGpsConfigured}
                            />
                        </CardContent>
                    </Card>
                </div>
            )}

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{m.vehicle_edit()}</DialogTitle>
                    </DialogHeader>
                    <VehicleForm
                        vehicle={vehicle}
                        onSuccess={() => setEditOpen(false)}
                        onCancel={() => setEditOpen(false)}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}
